import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { openDB, IDBPDatabase } from 'idb';
import { ChatMessage } from '../../models/chat-message';
import { CrossTabSyncService } from '../cross-tab-sync.service';
import { ConversationManager } from '../conversation/conversation-manager.service';

/**
 * Single source of truth for messages.
 * All writes go through IndexedDB.
 * UI observes read-only stream.
 * NO array replacements - only incremental updates.
 *
 * Design Principle: IndexedDB is more reliable than backend Redis
 * - Permanent storage (no TTL)
 * - Survives browser/backend crashes
 * - Survives Redis pod restarts
 * - Device-local persistence
 */
@Injectable({ providedIn: 'root' })
export class MessageStoreService {
  private db!: IDBPDatabase;
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);

  // 🔥 FIX (FIN-0069): Track initialization completion to prevent race conditions
  // Tab 2 was querying IndexedDB before init() completed, getting 0 results
  private initComplete!: Promise<void>;

  // 📡 SOLUTION 2: Throttle timer for cross-tab reload events
  // Prevents excessive reloads during streaming (100ms quiet period)
  private reloadThrottle: number | null = null;

  // 🔥 REMOVED (FIN-0065): Dual source of truth architecture smell
  // This caused silent message rejection when out of sync with ConversationManager
  // Now we use ConversationManager.getCurrentConversationId() as single source of truth
  // private currentConversationId: string | null = null;

  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  // 🔬 SCIENTIFIC METHOD: Track subscription lifecycle
  // 🔥 FIX (FIN-0065): Inject ConversationManager to use as single source of truth
  constructor(
    private crossTabSync: CrossTabSyncService,
    private conversationManager: ConversationManager
  ) {
    // Monitor subscription count in real-time
    let observerCount = 0;
    const originalSubscribe = this.messagesSubject.subscribe.bind(this.messagesSubject);
    this.messagesSubject.subscribe = (...args: any[]) => {
      observerCount++;
      const subscription = originalSubscribe(...args);
      const originalUnsubscribe = subscription.unsubscribe.bind(subscription);
      subscription.unsubscribe = () => {
        observerCount--;
        originalUnsubscribe();
      };
      return subscription;
    };

    // 📡 CROSS-TAB SYNC: Listen for message content updates from other tabs
    // 🔥 FIX (FIN-0065): Use ConversationManager as single source of truth
    this.crossTabSync.listen('message-content-update', (data: {
      conversationId: string;
      messageId: string;
      content: string;
    }) => {
      // Only process if it's for the current conversation
      const currentConversationId = this.conversationManager.getCurrentConversationId();
      if (data.conversationId !== currentConversationId) {
        return;
      }

      // Find the message and update its BehaviorSubject
      const messages = this.messagesSubject.getValue();
      const message = messages.find(m => m.messageId === data.messageId);

      if (message) {
        const currentContent = message.content.getValue();
        if (currentContent !== data.content) {
          message.content.next(data.content);
        }
      }
    });

    // 📡 SOLUTION 2 & 3: Listen for IndexedDB writes from other tabs (throttled)
    // 🔥 CRITICAL FIX: When Tab 1 writes to IndexedDB, Tab 2 needs to reload
    // Without this, Tab 2 continues showing stale data (e.g., empty "Thinking..." messages)
    //
    // Throttling: Delay reload by 100ms after last write to avoid spam during streaming
    // When bot is streaming, we might get 10+ writes/second. Throttle ensures we only
    // reload once after the stream settles.
    //
    // 📡 SOLUTION 3: Removed 'indexeddb-updated' listener (atomic merge no longer exists)
    // Only 'message-updated' events from upsertMessage() now trigger cross-tab reloads

    this.crossTabSync.listen('message-updated', (data: {
      conversationId: string;
      messageId: string;
      action: string;
    }) => {
      // Only reload if it's for the current conversation
      const currentConversationId = this.conversationManager.getCurrentConversationId();
      if (data.conversationId !== currentConversationId) {
        console.log('[CrossTab] Ignoring write for different conversation');
        return;
      }

      // Clear existing throttle timer
      if (this.reloadThrottle) {
        clearTimeout(this.reloadThrottle);
      }

      // Schedule reload after 100ms of quiet
      this.reloadThrottle = setTimeout(() => {
        console.log('[CrossTab] Reloading after IndexedDB write from other tab');
        this.loadMessages(data.conversationId).catch(error => {
          console.error('[CrossTab] Failed to reload after write:', error);
        });
        this.reloadThrottle = null;
      }, 100) as any;
    });
  }

  async init(): Promise<void> {
    // 🔥 FIX (FIN-0069): Store initialization Promise for ready() gate
    // This allows other services to wait for init to complete before querying
    this.initComplete = this.performInit();
    return this.initComplete;
  }

  /**
   * 🔥 FIX (FIN-0069): Wait for MessageStore to be fully initialized
   * This prevents race conditions where ChatService queries IndexedDB
   * before the database is ready, getting 0 results.
   *
   * Usage:
   *   await messageStore.ready();
   *   const messages = await messageStore.getMessages(conversationId);
   */
  async ready(): Promise<void> {
    return this.initComplete;
  }

  private async performInit(): Promise<void> {
    this.db = await openDB('finderwise-conversations', 2, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Clean up old object stores if upgrading
        if (oldVersion < 2) {
          const storeNames = Array.from(db.objectStoreNames);
          for (const name of storeNames) {
            db.deleteObjectStore(name);
          }
        }

        // Create messages store with proper indexes
        if (!db.objectStoreNames.contains('messages')) {
          const store = db.createObjectStore('messages', { keyPath: 'messageId' });
          store.createIndex('by-conversation', 'conversationId', { unique: false });
          store.createIndex('by-timestamp', 'timestamp', { unique: false });
          store.createIndex('by-conversation-and-timestamp', ['conversationId', 'timestamp'], { unique: false });
        }

        // Create conversation metadata store
        if (!db.objectStoreNames.contains('conversation-meta')) {
          db.createObjectStore('conversation-meta', { keyPath: 'conversationId' });
        }
      }
    });

    // 🚀 PERFORMANCE FIX: Load conversation ID only (not full messages)
    // This prevents 4-10 second delay while avoiding flicker
    // Messages will load lazily when needed
    await this.loadMostRecentConversationId();
  }

  /**
   * 🚀 PERFORMANCE FIX: Load only the conversation ID, not messages
   * This is called during init() to be fast (~50ms instead of 4-10 seconds)
   * Messages are loaded lazily later to avoid blocking UI render
   * 🔥 FIX (FIN-0065): Uses ConversationManager which already initialized from localStorage
   * No need to load from IndexedDB - ConversationManager is the source of truth
   */
  async loadMostRecentConversationId(): Promise<void> {
    // 🔥 FIX (FIN-0065): ConversationManager already initialized the conversation ID
    // from localStorage in its constructor. We don't need to duplicate that logic here.
    // Just load the messages for whatever conversation is currently active.
    const currentConversationId = this.conversationManager.getCurrentConversationId();

    if (!currentConversationId) {
      // No active conversation - nothing to load
      return;
    }

    try {
      // 🚀 FIX: Load messages in BACKGROUND - don't block init()!
      // This prevents 2-15s delay during page refresh under parallel load
      // UI will show loading state until messages appear
      this.loadMessages(currentConversationId).then(() => {
      }).catch(error => {
      });
    } catch (error) {
    }
  }

  // 🔥 REMOVED (FIN-0065): setActiveConversation() method eliminated
  // This was causing dual source of truth architecture smell
  // All conversation ID management now goes through ConversationManager
  // Use conversationManager.setActiveConversationId() instead

  /**
   * Load messages for the current conversation.
   * This is called internally after writes to refresh the observable.
   * 🔥 FIX: Updates existing messages IN PLACE to preserve BehaviorSubject references.
   * 🔥 FIX (FIN-0065): Uses ConversationManager as single source of truth
   */
  async loadMessages(conversationId?: string): Promise<void> {
    // 🔥 FIX (FIN-0065): Get current conversation from ConversationManager
    const targetConversationId = conversationId || this.conversationManager.getCurrentConversationId();

    if (!targetConversationId) {
      this.messagesSubject.next([]);
      return;
    }

    try {
      // Query messages for this conversation
      // All messages MUST have a conversationId (generated by UI for new conversations)
      const tx = this.db.transaction('messages', 'readonly');
      const index = tx.store.index('by-conversation');

      // Load only messages with the exact conversationId match
      const stored = await index.getAll(targetConversationId);

      // Sort by timestamp (oldest first)
      // 🔥 FIX: Use robust timestamp parsing that handles both:
      // - ISO strings from frontend: "2025-11-25T00:07:34.000000000Z"
      // - Numeric timestamps from backend: 1764030378872 or "1764030378872"
      stored.sort((a, b) => {
        const timeA = this.parseTimestamp(a.timestamp);
        const timeB = this.parseTimestamp(b.timestamp);
        return timeA - timeB;
      });

      // 🧹 CLEANUP: Remove OLD empty bot messages (but keep recent ones that might be streaming)
      // Reason: Empty bot messages from old sessions will never receive content because Tab1 and Tab2
      // have separate BehaviorSubject instances. If we load an empty message, it will stay
      // empty forever and show "Thinking..." indefinitely.
      //
      // ⚠️ CRITICAL: Don't delete empty bot messages less than 60 seconds old!
      // When Tab 2 opens DURING active streaming, the bot message might still be empty in IndexedDB
      // because chunks haven't been persisted yet. The message will receive content via WebSocket
      // streaming, so we MUST keep it.
      //
      // 60 seconds grace period handles:
      // - Long streaming responses (10-15+ seconds)
      // - Network delays
      // - Multiple concurrent tabs opening during streaming
      const orphanedMessages: string[] = [];
      const now = new Date().getTime();
      const GRACE_PERIOD_MS = 60000; // 60 seconds - generous for long streaming responses

      const cleanedMessages = stored.filter(msg => {
        if (!msg.bot) return true; // Keep all user messages
        if (msg.content && msg.content.length > 0) return true; // Keep bot messages with content

        // Calculate age of the message
        const messageAge = now - new Date(msg.timestamp).getTime();

        // Keep empty bot messages that are less than 60 seconds old (likely still streaming)
        if (messageAge < GRACE_PERIOD_MS) {
          return true; // Keep recent empty bot message (might be streaming)
        }

        // Remove OLD empty bot messages (truly orphaned from previous sessions)
        orphanedMessages.push(msg.messageId);
        return false; // Remove this old empty bot message
      });

      // Delete orphaned messages from IndexedDB
      if (orphanedMessages.length > 0) {
        const deleteTx = this.db.transaction('messages', 'readwrite');
        for (const messageId of orphanedMessages) {
          await deleteTx.store.delete(messageId);
        }
        await deleteTx.done;
      }

      // 🔥 FIX: Update existing messages IN PLACE, create new ones only when needed
      // This preserves BehaviorSubject references that the processor is updating
      const currentMessages = this.messagesSubject.getValue();
      const updatedMessages: ChatMessage[] = [];

      for (const storedMessage of cleanedMessages) {
        // Find existing message by messageId
        const existingMessage = currentMessages.find(m => m.messageId === storedMessage.messageId);

        if (existingMessage) {
          // 🔥 CRITICAL: Update existing message IN PLACE
          // Only update the content if it has changed (preserve BehaviorSubject reference)
          const currentContent = existingMessage.content.getValue();
          const storedContent = storedMessage.content || '';

          if (currentContent !== storedContent) {
            existingMessage.content.next(storedContent);
          }

          // Update other properties that might have changed
          existingMessage.conversationId = storedMessage.conversationId;
          existingMessage.timestamp = storedMessage.timestamp;
          (existingMessage as any).sequenceNumber = storedMessage.sequenceNumber || 0;
          (existingMessage as any).chunks = storedMessage.chunks || [];
          (existingMessage as any).chunksSequenceNumbers = storedMessage.chunksSequenceNumbers || [];
          (existingMessage as any).tracingContext = storedMessage.tracingContext;
          (existingMessage as any).messageType = storedMessage.messageType;

          updatedMessages.push(existingMessage);
        } else {
          // Create new message only if it doesn't exist yet
          const newMessage = this.deserializeMessage(storedMessage);

          // 📡 CROSS-TAB SYNC: Wrap the deserialized message for broadcasting
          // This ensures that messages loaded from IndexedDB can receive cross-tab updates
          this.ensureMessageContentWrapped(newMessage);

          updatedMessages.push(newMessage);
        }
      }

      // Emit updated array (with preserved BehaviorSubject references)
      this.messagesSubject.next(updatedMessages);

    } catch (error) {
      this.messagesSubject.next([]);
    }
  }

  /**
   * Upsert a single message (WebSocket chunk, user message, etc.)
   * NEVER replaces entire array - only updates one message.
   *
   * This is the ONLY way to modify messages.
   * 🔥 FIX: Updates in-memory array directly instead of reloading from IndexedDB.
   * 📡 CROSS-TAB SYNC: Wraps message BehaviorSubject to broadcast updates.
   */
  async upsertMessage(message: ChatMessage): Promise<void> {
    if (!message.messageId) {
      return;
    }

    try {
      // 📡 CROSS-TAB SYNC: Ensure the message's BehaviorSubject is wrapped for broadcasting
      this.ensureMessageContentWrapped(message);

      // 1. Write to IndexedDB (source of truth for persistence)
      // 🔥 FIX (FIN-0068 Phase 1): Persist bot messages only after they have content
      // This prevents empty "Thinking..." messages from being saved and shown in Tab 2
      //
      // Behavior:
      // - User messages: Always persist immediately (never empty)
      // - Bot messages: Persist ONLY when they have content (after first streaming chunk)
      //
      // Why: Bot messages start empty during streaming initialization. If we persist them
      // immediately, Tab 2 will load the empty "Thinking..." state instead of the actual
      // bot response. By waiting for first content, we ensure Tab 2 sees real responses.
      //
      // Note: Empty bot messages older than 60 seconds are cleaned up in loadMessages()
      // via orphan detection as a safety net for any edge cases.
      const content = message.content.getValue();
      const shouldPersist = !message.bot || (content && content.trim().length > 0);

      if (shouldPersist) {
        await this.db.put('messages', this.serializeMessage(message));

        // 📡 SOLUTION 2: Broadcast to other tabs on every write
        // This notifies other tabs to reload from IndexedDB, ensuring multi-tab sync
        this.crossTabSync.broadcast('message-updated', {
          conversationId: message.conversationId,
          messageId: message.messageId,
          action: 'upsert'
        });
      }

      // 2. 🔥 FIX: Update in-memory array directly (don't reload from IndexedDB)
      // This preserves BehaviorSubject references that the processor is actively updating
      const currentMessages = this.messagesSubject.getValue();

      // 🔥 CRITICAL (FIN-0065): Get current conversation from ConversationManager (single source of truth)
      const currentConversationId = this.conversationManager.getCurrentConversationId();

      // 🔥 DEFENSIVE WARNING (FIN-0065): Catch conversation ID mismatches
      if (message.conversationId !== currentConversationId) {
        console.error('🚨 MESSAGE REJECTED - Conversation ID mismatch!', {
          messageConversationId: message.conversationId,
          currentConversationId: currentConversationId,
          messageId: message.messageId,
          messageContent: message.content.getValue().substring(0, 100),
          stackTrace: new Error().stack
        });
        // Don't add message to UI if it doesn't belong to current conversation
        return;
      }

      // 🔥 CRITICAL: Filter to only messages for the current conversation
      // This prevents mixing messages from different conversations
      const currentConversationMessages = currentMessages.filter(m =>
        m.conversationId === currentConversationId
      );

      const existingIndex = currentConversationMessages.findIndex(m => m.messageId === message.messageId);

      if (existingIndex >= 0) {
        // Message already exists - keep the same reference (preserves BehaviorSubject)
        // No need to update anything - the reference is already in the array
      } else {
        // New message - add to array (only if it belongs to current conversation)
        if (message.conversationId === currentConversationId) {
          const newMessages = [...currentConversationMessages, message];
          // Sort by timestamp (oldest first)
          // 🔥 FIX: Use robust timestamp parsing for mixed format handling
          newMessages.sort((a, b) => {
            const timeA = this.parseTimestamp(a.timestamp);
            const timeB = this.parseTimestamp(b.timestamp);
            return timeA - timeB;
          });
          this.messagesSubject.next(newMessages);
        }
      }
    } catch (error) {
    }
  }

  /**
   * Bulk upsert (for initial load or sync).
   * Still incremental - updates each message individually.
   * 🔥 FIX: Updates in-memory array directly instead of reloading from IndexedDB.
   * 📡 CROSS-TAB SYNC: Wraps message BehaviorSubjects to broadcast updates.
   */
  async upsertMessages(messages: ChatMessage[]): Promise<void> {
    if (!messages || messages.length === 0) {
      return;
    }

    try {
      const tx = this.db.transaction('messages', 'readwrite');

      // Collect all unique conversationIds from the messages
      const conversationIds = new Set<string>();

      // Write each message individually (no "replace all")
      // 🔥 FIX: Skip empty bot messages (same as upsertMessage)
      // 📡 CROSS-TAB SYNC: Wrap each message's BehaviorSubject
      for (const msg of messages) {
        if (msg.messageId) {
          // Ensure the message's BehaviorSubject is wrapped for broadcasting
          this.ensureMessageContentWrapped(msg);

          const content = msg.content.getValue();
          const isEmptyBotMessage = msg.bot && (!content || content.trim() === '');

          if (!isEmptyBotMessage) {
            await tx.store.put(this.serializeMessage(msg));
          }

          if (msg.conversationId) {
            conversationIds.add(msg.conversationId);
          }
        }
      }

      await tx.done;

      // 🔥 REMOVED (FIN-0065): Automatic conversation ID update logic
      // ConversationManager is now the single source of truth - we don't manage conversation ID here

      // 🔥 FIX: Update in-memory array directly (don't reload from IndexedDB)
      // This preserves BehaviorSubject references that the processor is actively updating
      const currentMessages = this.messagesSubject.getValue();

      // 🔥 CRITICAL (FIN-0065): Get current conversation from ConversationManager (single source of truth)
      const currentConversationId = this.conversationManager.getCurrentConversationId();

      // 🔥 CRITICAL: Filter to only messages for the current conversation
      // This prevents mixing messages from different conversations
      const currentConversationMessages = currentMessages.filter(m =>
        m.conversationId === currentConversationId
      );

      const messageMap = new Map(currentConversationMessages.map(m => [m.messageId, m]));

      // Add or update messages
      for (const msg of messages) {
        if (msg.messageId && !messageMap.has(msg.messageId)) {
          // 🔥 DEFENSIVE WARNING (FIN-0065): Warn about conversation ID mismatches
          if (msg.conversationId !== currentConversationId) {
            console.warn('⚠️ Skipping message with mismatched conversation ID in bulk upsert', {
              messageConversationId: msg.conversationId,
              currentConversationId: currentConversationId,
              messageId: msg.messageId
            });
            continue; // Skip this message
          }

          // New message - add it
          messageMap.set(msg.messageId, msg);
        }
        // If message already exists, keep the existing reference (preserves BehaviorSubject)
      }

      // Convert back to array and sort by timestamp
      // 🔥 FIX: Use robust timestamp parsing for mixed format handling
      const updatedMessages = Array.from(messageMap.values());
      updatedMessages.sort((a, b) => {
        const timeA = this.parseTimestamp(a.timestamp);
        const timeB = this.parseTimestamp(b.timestamp);
        return timeA - timeB;
      });

      // Only emit if the conversation matches
      if (messages.some(m => m.conversationId === currentConversationId)) {
        this.messagesSubject.next(updatedMessages);
      }
    } catch (error) {
    }
  }

  /**
   * Clear all messages for a specific conversation (new conversation).
   * 🔥 FIX (FIN-0065): Uses ConversationManager as single source of truth
   */
  async clearMessages(conversationId?: string): Promise<void> {
    // 🔥 FIX (FIN-0065): Get current conversation from ConversationManager
    const targetConversationId = conversationId || this.conversationManager.getCurrentConversationId();

    if (!targetConversationId) {
      return;
    }

    try {
      // Delete all messages for this conversation
      const tx = this.db.transaction('messages', 'readwrite');
      const index = tx.store.index('by-conversation');
      const keys = await index.getAllKeys(targetConversationId);

      for (const key of keys) {
        await tx.store.delete(key);
      }

      await tx.done;

      // 🔥 FIX: Reload messages from IndexedDB after clearing
      // This ensures UI updates even if subscription was lost (e.g., after page refresh)
      // loadMessages() will read empty array from IndexedDB and emit it properly
      await this.loadMessages(targetConversationId);
    } catch (error) {
    }
  }

  /**
   * 📡 SOLUTION 3: Check if conversation has any messages in IndexedDB
   * Used to determine if we need to load from backend (cold backup)
   *
   * @param conversationId Conversation ID to check
   * @returns true if no messages exist, false if messages exist
   */
  async isEmpty(conversationId: string): Promise<boolean> {
    await this.ready();
    const tx = this.db.transaction('messages', 'readonly');
    const index = tx.store.index('by-conversation');
    const count = await index.count(conversationId);
    return count === 0;
  }

  /**
   * Get all messages for a conversation (for backend sync).
   */
  async getMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      const tx = this.db.transaction('messages', 'readonly');
      const index = tx.store.index('by-conversation');
      const stored = await index.getAll(conversationId);

      // Sort by timestamp
      // 🔥 FIX: Use robust timestamp parsing for mixed format handling
      stored.sort((a, b) => {
        const timeA = this.parseTimestamp(a.timestamp);
        const timeB = this.parseTimestamp(b.timestamp);
        return timeA - timeB;
      });

      return stored.map(s => this.deserializeMessage(s));
    } catch (error) {
      return [];
    }
  }

  /**
   * 📡 SOLUTION 3: Removed mergeMessages() and deduplicateMessages() (~118 lines)
   *
   * Backend is now pull-only (cold backup):
   * - loadMessagesFromBackend() uses isEmpty() to check if local data exists
   * - If local exists, skip backend (IndexedDB is ahead)
   * - If empty, load from backend using upsertMessage()
   *
   * Benefits:
   * - Simpler architecture (1 write path instead of 3)
   * - No merge conflicts or race conditions
   * - Clearer mental model (IndexedDB is always current)
   * - ~180 lines of complex logic removed
   */

  /**
   * Get current message count (without loading all messages).
   * 🔥 FIX (FIN-0065): Uses ConversationManager as single source of truth
   */
  async getMessageCount(conversationId?: string): Promise<number> {
    // 🔥 FIX (FIN-0065): Get current conversation from ConversationManager
    const targetConversationId = conversationId || this.conversationManager.getCurrentConversationId();

    if (!targetConversationId) {
      return 0;
    }

    try {
      const tx = this.db.transaction('messages', 'readonly');
      const index = tx.store.index('by-conversation');
      const count = await index.count(targetConversationId);
      return count;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Check if we have any messages for a conversation.
   */
  async hasMessages(conversationId?: string): Promise<boolean> {
    const count = await this.getMessageCount(conversationId);
    return count > 0;
  }

  /**
   * Serialize message for IndexedDB storage.
   * Converts BehaviorSubject content to string.
   */
  private serializeMessage(msg: ChatMessage): any {
    return {
      messageId: msg.messageId,
      conversationId: msg.conversationId,
      content: msg.content.getValue(), // BehaviorSubject → string
      bot: msg.bot,
      pictureUrl: msg.pictureUrl,
      timestamp: msg.timestamp,
      sequenceNumber: (msg as any).sequenceNumber || 0,
      chunks: (msg as any).chunks || [],
      chunksSequenceNumbers: (msg as any).chunksSequenceNumbers || [],
      tracingContext: (msg as any).tracingContext,
      messageType: (msg as any).messageType
    };
  }

  /**
   * Deserialize message from IndexedDB storage.
   * Converts string content to BehaviorSubject.
   *
   * 📡 CROSS-TAB SYNC: Wraps BehaviorSubject to broadcast updates to other tabs.
   */
  private deserializeMessage(stored: any): ChatMessage {
    const contentSubject = new BehaviorSubject<string>(stored.content || '');

    // DON'T wrap during deserialization to prevent broadcast storm
    // When Tab 2 loads 100+ messages from IndexedDB, wrapping each one
    // would trigger 100+ broadcasts, overwhelming the browser.
    // Instead, messages will be wrapped lazily via ensureMessageContentWrapped()
    // when they actually need to receive updates from WebSocket.

    return {
      messageId: stored.messageId,
      conversationId: stored.conversationId,
      content: contentSubject,  // NOT WRAPPED - will be wrapped on-demand
      bot: stored.bot,
      pictureUrl: stored.pictureUrl,
      timestamp: stored.timestamp,
      sequenceNumber: stored.sequenceNumber || 0,
      chunks: stored.chunks || [],
      chunksSequenceNumbers: stored.chunksSequenceNumbers || [],
      tracingContext: stored.tracingContext,
      messageType: stored.messageType
    } as ChatMessage;
  }

  /**
   * 📡 CROSS-TAB SYNC: Ensure a message's BehaviorSubject is wrapped for broadcasting.
   *
   * This method is idempotent - safe to call multiple times on the same message.
   * It checks if the subject is already wrapped before wrapping it.
   *
   * 🔥 FIX (FIN-0068 Phase 1): Also subscribes to bot message content changes to persist
   * them to IndexedDB after they receive content from streaming.
   */
  private ensureMessageContentWrapped(message: ChatMessage): void {
    // Check if already wrapped by looking for our marker property
    if ((message.content as any).__crossTabWrapped) {
      console.log('[CrossTab] Message already wrapped, skipping:', message.messageId);
      return; // Already wrapped, skip
    }

    // Ensure we have required IDs
    if (!message.messageId) {
      console.warn('[CrossTab] Cannot wrap message - missing messageId');
      return;
    }

    console.log('[CrossTab] Wrapping message BehaviorSubject for broadcasting:', message.messageId);

    // Wrap the BehaviorSubject
    this.wrapBehaviorSubjectWithBroadcast(
      message.content,
      message.messageId,
      message.conversationId || ''
    );

    // 🔥 FIX (FIN-0068 Phase 1): Subscribe to bot message content changes
    // When a bot message receives content from streaming, persist it to IndexedDB
    if (message.bot) {
      let lastPersistedContent = '';
      let persistTimer: any = null;

      message.content.subscribe(content => {
        // Skip if content is empty (initial state before streaming starts)
        if (!content || content.trim().length === 0) {
          return;
        }

        // Check if content actually changed (avoid persisting identical content multiple times)
        if (content === lastPersistedContent) {
          return;
        }

        // Debounce persistence to avoid overwhelming IndexedDB during rapid streaming
        // Persist after 500ms of no new content (end of streaming burst)
        if (persistTimer) {
          clearTimeout(persistTimer);
        }

        persistTimer = setTimeout(() => {
          lastPersistedContent = content;
          // Persist to IndexedDB (async, don't block UI)
          this.db.put('messages', this.serializeMessage(message)).catch(err => {
            console.error('[MessageStore] Failed to persist bot message content:', err);
          });
        }, 500);
      });
    }

    // Mark as wrapped
    (message.content as any).__crossTabWrapped = true;
    console.log('[CrossTab] Message wrapped successfully:', message.messageId);
  }

  /**
   * 📡 CROSS-TAB SYNC: Wrap a BehaviorSubject to broadcast updates to other tabs.
   *
   * When content.next() is called, it will:
   * 1. Update the local BehaviorSubject
   * 2. Broadcast the update to all other tabs
   *
   * This ensures all tabs stay synchronized in real-time.
   */
  private wrapBehaviorSubjectWithBroadcast(
    subject: BehaviorSubject<string>,
    messageId: string,
    conversationId: string
  ): BehaviorSubject<string> {
    // Intercept the next() method
    const originalNext = subject.next.bind(subject);

    subject.next = (value: string) => {
      // Call original next() to update local subscribers
      originalNext(value);

      // Broadcast to other tabs
      this.crossTabSync.broadcast('message-content-update', {
        conversationId,
        messageId,
        content: value
      });
    };

    return subject;
  }

  /**
   * Get current messages synchronously (from last loaded state).
   */
  getCurrentMessages(): ChatMessage[] {
    return this.messagesSubject.getValue();
  }

  /**
   * Save conversation metadata.
   */
  async saveConversationMeta(conversationId: string, userEmail: string): Promise<void> {
    try {
      await this.db.put('conversation-meta', {
        conversationId,
        userEmail,
        lastModified: new Date().toISOString()
      });
    } catch (error) {
    }
  }

  /**
   * Get conversation metadata.
   */
  async getConversationMeta(conversationId: string): Promise<any> {
    try {
      return await this.db.get('conversation-meta', conversationId);
    } catch (error) {
      return null;
    }
  }

  /**
   * 🔥 FIX: Parse timestamp robustly to handle mixed formats.
   *
   * Problem: Backend returns numeric timestamps (1764030378872) while frontend
   * creates ISO strings ("2025-11-25T00:07:34.000000000Z"). When sorting:
   * - new Date("2025-11-25T00:07:34.000000000Z").getTime() → valid number
   * - new Date(1764030378872).getTime() → valid number (if passed as number)
   * - new Date("1764030378872").getTime() → NaN (string numeric fails!)
   *
   * NaN comparisons cause unpredictable sorting, leading to wrong message order
   * (e.g., all user messages appearing before bot messages).
   *
   * @param timestamp - Can be: ISO string, number, or numeric string
   * @returns Epoch milliseconds, or 0 if parsing fails
   */
  private parseTimestamp(timestamp: any): number {
    if (!timestamp) return 0;

    // If it's already a number, return it directly
    if (typeof timestamp === 'number') {
      return timestamp;
    }

    // If it's a string, try to parse it
    if (typeof timestamp === 'string') {
      // First try parsing as ISO date string (handles "2025-11-25T00:07:34.000000000Z")
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }

      // If ISO parsing failed, try parsing as numeric string (handles "1764030378872")
      const numericValue = parseInt(timestamp, 10);
      if (!isNaN(numericValue)) {
        return numericValue;
      }
    }

    // Fallback to 0 if all parsing fails
    return 0;
  }
}
